import { Injectable, isDevMode } from '@angular/core';
import { DesksTurnsDaoService } from 'src/app/dao/turns-dao-services/desks-turns-dao-service/desks-turns-dao.service';
import { EMPTY, Subject } from 'rxjs';
import { Schedule } from 'src/app/models/schedule-models/schedule.model';
import { Desk } from 'src/app/models/turns-models/desk.model';
import { FormGroup } from '@angular/forms';
import { UserService } from 'src/app/services/user-service/user.service';
import { DeviceBasic } from 'src/app/models/device-models/device-basic-model/device-basic.model';
import { DeskType } from 'src/app/models/turns-models/desk-type.model';
import { Location } from 'src/app/models/turns-models/location.model';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';

@Injectable()
export class DesksTurnsService {

  accessToken = ['TUR', 'CTR'];
  refreshDesksListener = new Subject<any>();
  refreshDeskListener = new Subject<any>();
  refreshDeskSchedulesListener = new Subject<any>();
  rememberDeskInfoFromScheduleListListener = new Subject<any>();
  searchDesksListener = new Subject<any>();
  filterDesksListener = new Subject<any>();
  rememberedDeskImage: string;
  rememberedDeskForm: FormGroup;
  rememberedSchedules = new Array<Schedule>();
  rememberedSchedule = new Schedule();
  toggleDesksDispositionListener = new Subject<any>();

  constructor(private desksTurnsDaoService: DesksTurnsDaoService,
              private userStorageService: UserStorageService,
              private userService: UserService) { }

  public getDesks() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const desks = new Array<Desk>();
          o['data']['deskList'].forEach(
            (d) => {
              const desk = new Desk();
              Object.assign(desk, d);
              desk.InitialCount = parseInt(d.initialCount, 10);
              desk.LimitCount = parseInt(d.limitCount, 10);
              desks.push(desk);
            }
          );
          return desks;
        })
      );
    }
    return EMPTY;
  }

  public getDesksStatus() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDesksStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            availableDesks: o['data']['deskList']['nbAvailables'],
            unavailableDesks: o['data']['deskList']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }

  public getDeskInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const desk = Object.assign(new Desk(), o['data']['deskTurnDetails']);
          const devicesBound = new Array<DeviceBasic>();
          const locationsBound = new Array<Location>();
          o['data']['deskTurnDetails']['devicesBound'].forEach(
            (item) => {
              devicesBound.push(Object.assign(new DeviceBasic(), item));
            }
          );
          o['data']['deskTurnDetails']['locationsBound'].forEach(
            (item) => {
              locationsBound.push(Object.assign(new Location(), item));
            }
          );
          desk.DevicesBound = devicesBound;
          desk.LocationsBound = locationsBound;
          return desk;
        })
      );
    }
    return EMPTY;
  }

  public addNewDesk(active, image, deskName, centerInternalId, deskTypeInternalId, voiceActivate, voicePrefix, deskPrefix, canAskCustomerName, dailyReset, smsNotifyDesk, smsInternalId, notifyPrevious, notifyLast, canPrint, ticketInternalId, countAutoReset, initialCount, limitCount, schedulesList, showTurnsAtTotem, availableTurn, autoAdvance, dataType, hasVip) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.addNewDesk(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, image, deskName, centerInternalId, deskTypeInternalId, voiceActivate, voicePrefix, deskPrefix, canAskCustomerName, dailyReset, smsNotifyDesk, smsInternalId, notifyPrevious, notifyLast, canPrint, ticketInternalId, countAutoReset, initialCount, limitCount, schedulesList, showTurnsAtTotem, availableTurn, autoAdvance, dataType, hasVip).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskInfo(internalId, active, deskName, centerInternalId, deskTypeInternalId, deskPrefix, canAskCustomerName, dailyReset, voiceActivate, voicePrefix, smsNotifyDesk, smsInternalId, notifyPrevious, notifyLast, canPrint, ticketInternalId, showTurnsAtTotem, availableTurn, autoAdvance, dataType, hasVip) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, deskName, centerInternalId, deskTypeInternalId, deskPrefix, canAskCustomerName, dailyReset, voiceActivate, voicePrefix, smsNotifyDesk, smsInternalId, notifyPrevious, notifyLast, canPrint, ticketInternalId, showTurnsAtTotem, availableTurn, autoAdvance, dataType, hasVip).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskAutoResetInfo(internalId, countAutoReset, initialCount, limitCount) {
    if (this.userService.accessGranted(this.accessToken)) {
      initialCount = initialCount.toString();
      limitCount = limitCount.toString();
      return this.desksTurnsDaoService.updateDeskAutoResetInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, countAutoReset, initialCount, limitCount).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskDeskNameTranslation(translatedNames) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskDeskNameTranslation(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), translatedNames).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskSchedules(internalId, centerInternalId, active, deskSchedules) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerInternalId, active, deskSchedules).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskStatus(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskCapacity(internalId, deskCapacity) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskCapacity(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, deskCapacity).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteDesk(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.deleteDesk(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateImageDesk(internalId, imageRaw, size) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateImageDesk(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, imageRaw, size).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public rebootDesk(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.rebootDesk(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDeskScheduleList(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskScheduleList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (s) => {
              schedules.push(Object.assign(new Schedule(), s));
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public getDeskScheduleInfo(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskScheduleInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addNewDeskSchedule(relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.addNewDeskSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeskSchedule(internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.updateDeskSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteDeskSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.deleteDeskSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDeskDevices(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDeskTypes() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskTypes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const deskTypes = new Array<DeskType>();
          o['data']['turnTypeList'].forEach(
            (t) => {
              deskTypes.push(Object.assign(new DeskType(), t));
            }
          );
          return deskTypes;
        })
      );
    }
    return EMPTY;
  }

  public saveDeskDevices(internalId, devicesInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.saveDeskDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, devicesInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDeskLocations(centerInternalId, turnTypeInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskLocations(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId, turnTypeInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const locations = new Array<Location>();
          o['data']['deskLocationList'].forEach(
            (l) => {
              locations.push(Object.assign(new Location(), l));
            }
          );
          return locations;
        })
      );
    }
    return EMPTY;
  }

  public saveDeskLocations(internalId, locationsBound) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.saveDeskLocations(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, locationsBound).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public pushTurn(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.pushTurn(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public callBackLastTurn(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.callBackLastTurn(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDeskExistingSchedules(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.getDeskExistingSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (ct) => {
              const schedule = new Schedule();
              Object.assign(schedule, ct);
              schedules.push(schedule);
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public linkExistingDeskSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.desksTurnsDaoService.linkExistingDeskSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public rememberDeskInfo(image, deskForm) {
    this.rememberedDeskImage = image;
    this.rememberedDeskForm = deskForm;
  }

  public getRememberedDeskInfo() {
    return { rememberedDeskImage: this.rememberedDeskImage, rememberedDeskForm: this.rememberedDeskForm };
  }

  public toRememberedSchedules(schedule) {
    this.rememberedSchedules.push(schedule);
  }

  public rememberSchedule(schedule) {
      this.rememberedSchedule = schedule;
  }

  public getRememberedSchedules() {
    return this.rememberedSchedules;
  }

  public getRememberedSchedule() {
    return this.rememberedSchedule;
  }

  public deleteRememberedSchedule(schedule) {
    this.rememberedSchedules.splice(
      this.rememberedSchedules.findIndex(
        (s) => {
          return schedule.CommentNote === s.CommentNote;
        }
      ), 1);
  }

  public deleteAllRememberedSchedule() {
    this.rememberedSchedules = new Array<Schedule>();
  }

  public rememberDeskInfoFromSchedule() {
    this.rememberDeskInfoFromScheduleListListener.next();
  }

  public rememberDeskInfoFromScheduleListen() {
    return this.rememberDeskInfoFromScheduleListListener.asObservable();
  }

  public refreshDesks() {
    this.refreshDesksListener.next();
  }

  public refreshDesksListen() {
    return this.refreshDesksListener.asObservable();
  }

  public refreshDesk() {
    this.refreshDeskListener.next();
  }

  public refreshDeskListen() {
    return this.refreshDeskListener.asObservable();
  }

  public refreshDeskSchedules() {
    this.refreshDeskSchedulesListener.next();
  }

  public refreshDeskSchedulesListen() {
    return this.refreshDeskSchedulesListener.asObservable();
  }

  public searchDesks(filterKey) {
    this.searchDesksListener.next(filterKey);
  }

  public searchDesksListen() {
    return this.searchDesksListener.asObservable();
  }

  public filterDesks(opts) {
    this.filterDesksListener.next(opts);
  }

  public filterDesksListen() {
    return this.filterDesksListener.asObservable();
  }

  public toggleDesksDisposition(viewDis) {
    this.toggleDesksDispositionListener.next(viewDis);
  }

  public toggleDesksDispositionListen() {
    return this.toggleDesksDispositionListener.asObservable();
  }

}
